import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  resolveHomePortal,
  PORTAL_HOME,
  type PortalId,
} from "@/router/portals";
import { usePortalGuard } from "@livingsyncs/ui/src/lib/crossAppRedirect";
import { FullPageLoader } from "./FullPageLoader";

interface PortalBoundaryProps {
  portal: PortalId;
}

/**
 * Portal-membership boundary (US-044 AC8). Runs once at a portal's layout
 * route: if the user's home portal is not this one, sign them out
 * (usePortalGuard) and send them to /login with an inline error naming the
 * right portal — never silently render/redirect into it.
 *
 * The loading guard is load-bearing (AC16): while `useAuth` is unresolved it
 * renders a loader and makes **zero** access decision — deciding on an
 * undefined context is the root cause of the /admin 403 + FacilitySwitcher
 * flash bugs.
 *
 * Replaces the negative `NonAdminRoute`/`AdminRoute`/`AuditorRoute` guards
 * with one positive check driven by the shared `resolveHomePortal`.
 */
export function PortalBoundary({ portal }: PortalBoundaryProps) {
  const { data: context, isLoading } = useAuth();
  const home = context ? resolveHomePortal(context) : null;
  const status = usePortalGuard(home, portal);

  if (isLoading || !context || status === "checking") {
    return <FullPageLoader />;
  }

  if (status === "wrong-portal") {
    return <Navigate to="/login" state={{ wrongPortalHome: home }} replace />;
  }

  // A user has exactly one home portal. Note (US-044, intentional): platform
  // admins carry all auditorGroupIds, so the old AuditorRoute let them into
  // /audit; resolveHomePortal puts them in `admin`, so a platform admin
  // hitting audit's own PortalBoundary is `wrong-portal`, not `no-access`.
  // Accepted on purpose — they administer auditors from /admin/auditor-
  // groups, and no admin UI links into /audit.
  if (status === "no-access") {
    return <Navigate to={PORTAL_HOME["no-access"]} replace />;
  }

  return <Outlet />;
}
